/**
 * FlzExpertRooms — Expert agent rooms
 * GET /api/flzhub/expert-rooms → { rooms: [{id, agent_name, expertise, last_message, msg_count}] }
 * GET /api/flzhub/messages?agent_id=X → room messages
 */
import { ArrowLeft, MessageCircle, Send } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { API_URL, apiFetch } from '../../../config/api';
import { Message } from '../../../types/flzhub';
import { getAgentColor, getAgentInitials } from './Shared';

interface ExpertRoom {
  id: number
  agent_name: string
  expertise: string
  last_message: string
  msg_count: number
}

export const FlzExpertRooms: React.FC = () => {
  const [rooms, setRooms] = useState<ExpertRoom[]>([])
  const [loading, setLoading] = useState(true)
  const [activeRoom, setActiveRoom] = useState<ExpertRoom | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [msgLoading, setMsgLoading] = useState(false)

  useEffect(() => {
    apiFetch(`${API_URL}/api/flzhub/expert-rooms`)
      .then(r => r.json())
      .then(d => setRooms(d.rooms ?? []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const openRoom = async (room: ExpertRoom) => {
    setActiveRoom(room)
    setMsgLoading(true)
    try {
      const r = await apiFetch(`${API_URL}/api/flzhub/messages?agent_id=${room.id}&limit=50`)
      const d = await r.json()
      setMessages(d.messages ?? [])
    } catch { setMessages([]) }
    setMsgLoading(false)
  }

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--os-muted)', fontSize: 12 }}>Loading expert rooms...</div>
  )

  // Room detail view
  if (activeRoom) return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--os-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <button onClick={() => setActiveRoom(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--os-muted)', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
          <ArrowLeft size={13} /> Back
        </button>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: getAgentColor(activeRoom.agent_name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700 }}>
          {getAgentInitials(activeRoom.agent_name)}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700 }}>{activeRoom.agent_name}</div>
          <div style={{ fontSize: 9, color: 'var(--os-muted)' }}>{activeRoom.expertise}</div>
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--os-muted)' }}>{activeRoom.msg_count} messages</span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {msgLoading && <div style={{ textAlign: 'center', color: 'var(--os-muted)', fontSize: 11, padding: 20 }}>Loading...</div>}
        {!msgLoading && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--os-muted)', fontSize: 11, padding: 40 }}>No messages in this room yet</div>
        )}
        {messages.map(m => (
          <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: getAgentColor(m.agent_name ?? ''), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 8, fontWeight: 700, flexShrink: 0 }}>
              {getAgentInitials(m.agent_name ?? '')}
            </div>
            <div style={{ flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700 }}>{m.agent_name}</span>
                {m.eq_score && <span style={{ fontSize: 9, color: '#10b981' }}>EQ {m.eq_score.toFixed(1)}</span>}
                <span style={{ fontSize: 9, color: 'var(--os-muted)', marginLeft: 'auto' }}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div style={{ fontSize: 11, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>{m.message}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  // Room list
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 12 }}>
      <div style={{ fontSize: 11, color: 'var(--os-muted)', marginBottom: 12 }}>
        {rooms.length} expert rooms — each expert has a dedicated discussion space
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rooms.map(room => (
          <button key={room.id} onClick={() => openRoom(room)} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
            background: 'rgba(255,255,255,0.04)', border: '1px solid var(--os-border)',
            borderRadius: 10, cursor: 'pointer', textAlign: 'left', color: 'inherit',
            transition: 'all 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.background = 'rgba(99,102,241,0.08)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--os-border)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)' }}
          >
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: getAgentColor(room.agent_name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
              {getAgentInitials(room.agent_name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{room.agent_name}</div>
              <div style={{ fontSize: 10, color: '#6366f1', marginBottom: 4 }}>{room.expertise}</div>
              <div style={{ fontSize: 10, color: 'var(--os-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {room.last_message?.slice(0, 80)}...
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--os-muted)' }}>
                <MessageCircle size={10} /> {room.msg_count}
              </div>
              <Send size={12} color="#6366f1" />
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
